import {
  UzButton,
  UzCard,
  UzCheckbox,
  UzDialog,
  UzInput,
  UzMoneyInput,
  UzSelect,
} from '@utazzo/ui';
import { Ban, PlusCircle, Printer } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import {
  useCancelCreditNote,
  useCreditNotes,
  useIssueCreditNote,
  useMarkCreditNoteUsed,
  useUnmarkCreditNoteUsed,
} from '../use-invoices';

import { rupeesToPaise } from './invoice-form.helpers';

import type {
  CreateCreditNoteRequest,
  CreditNoteKind,
  CreditNoteRow,
  InvoiceStatus,
} from '@utazzo/shared';

/**
 * Admin panel (screen-only, never printed) for issuing and listing credit notes
 * against a tax invoice. Rendered only when the invoice is a TAX_INVOICE - a
 * proforma has nothing to credit yet. Mirrors the InvoiceAdminPanel styling.
 */

const KIND_OPTIONS = [
  { value: 'FULL', label: 'Full - credit the whole remaining invoice' },
  { value: 'PARTIAL', label: 'Partial - credit a specific amount' },
] as const;

const KIND_LABEL: Record<CreditNoteKind, string> = {
  FULL: 'Full',
  PARTIAL: 'Partial',
};

export const CreditNotePanel = ({
  invoiceId,
  status,
}: {
  invoiceId: string;
  status: InvoiceStatus;
}): React.JSX.Element | null => {
  const creditNotesQuery = useCreditNotes(status === 'TAX_INVOICE' ? invoiceId : undefined);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (status !== 'TAX_INVOICE') return null;

  const rows = creditNotesQuery.data ?? [];

  return (
    <UzCard
      className="invoice-no-print mx-auto max-w-3xl"
      title="Credit notes"
      description="Reverse a tax invoice in full or in part. Each note reverses its share of the GST."
      actions={
        <UzButton
          size="sm"
          leftIcon={<PlusCircle className="h-4 w-4" />}
          onClick={() => setDialogOpen(true)}
        >
          Issue credit note
        </UzButton>
      }
    >
      {creditNotesQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading credit notes...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No credit notes issued against this invoice.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                <th className="py-2 pr-4">Number</th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Kind</th>
                <th className="py-2 pr-4">Reason</th>
                <th className="py-2 pr-4 text-right">CGST</th>
                <th className="py-2 pr-4 text-right">SGST</th>
                <th className="py-2 pr-4 text-right">IGST</th>
                <th className="py-2 pr-4 text-right">Total</th>
                <th className="py-2 pr-4">Utilised</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((cn) => (
                <CreditNoteRowLine key={cn.id} invoiceId={invoiceId} cn={cn} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IssueCreditNoteDialog invoiceId={invoiceId} open={dialogOpen} onOpenChange={setDialogOpen} />
    </UzCard>
  );
};

const CreditNoteRowLine = ({
  invoiceId,
  cn,
}: {
  invoiceId: string;
  cn: CreditNoteRow;
}): React.JSX.Element => {
  const cancel = useCancelCreditNote(invoiceId);
  const markUsed = useMarkCreditNoteUsed(invoiceId);
  const unmarkUsed = useUnmarkCreditNoteUsed(invoiceId);
  const isIssued = cn.status === 'ISSUED';
  const isUsed = cn.status === 'USED';

  const busyUsed = markUsed.isPending || unmarkUsed.isPending;
  const onToggleUtilised = (checked: boolean): void => {
    if (busyUsed) return;
    if (checked) markUsed.mutate({ id: cn.id, body: {} });
    else unmarkUsed.mutate(cn.id);
  };

  return (
    <tr className="border-b border-zinc-100 last:border-0">
      <td className="py-2 pr-4 font-medium whitespace-nowrap text-zinc-800 tabular-nums">
        {cn.creditNoteNumber}
      </td>
      <td className="py-2 pr-4 whitespace-nowrap tabular-nums">{formatDateDMY(cn.issuedAt)}</td>
      <td className="py-2 pr-4">{KIND_LABEL[cn.kind]}</td>
      <td className="py-2 pr-4">{cn.reason}</td>
      <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
        {formatInrPaise(cn.reversedCgstPaise)}
      </td>
      <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
        {formatInrPaise(cn.reversedSgstPaise)}
      </td>
      <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
        {formatInrPaise(cn.reversedIgstPaise)}
      </td>
      <td className="py-2 pr-4 text-right font-medium whitespace-nowrap tabular-nums">
        {formatInrPaise(cn.totalPaise)}
      </td>
      <td className="py-2 pr-4">
        {cn.status === 'CANCELLED' ? (
          <span className="text-xs font-medium text-red-600">Cancelled</span>
        ) : (
          <UzCheckbox
            label="Utilised"
            checked={isUsed}
            disabled={busyUsed}
            onCheckedChange={(c) => onToggleUtilised(Boolean(c))}
          />
        )}
      </td>
      <td className="py-2">
        <div className="flex items-center justify-end gap-1.5">
          <a
            href={`/invoices/credit-notes/${cn.id}/print`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
          >
            <Printer className="h-3.5 w-3.5" />
            Print
          </a>
          {isIssued && (
            <UzButton
              variant="ghost"
              size="sm"
              leftIcon={<Ban className="h-3.5 w-3.5" />}
              loading={cancel.isPending}
              onClick={() => cancel.mutate(cn.id)}
            >
              Cancel
            </UzButton>
          )}
        </div>
      </td>
    </tr>
  );
};

const IssueCreditNoteDialog = ({
  invoiceId,
  open,
  onOpenChange,
}: {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element => {
  const issue = useIssueCreditNote(invoiceId);

  const [kind, setKind] = useState<CreditNoteKind>('FULL');
  const [amount, setAmount] = useState<number | ''>('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setKind('FULL');
      setAmount('');
      setReason('');
    }
  }, [open]);

  const amountValid = kind === 'FULL' || (typeof amount === 'number' && amount > 0);
  const reasonValid = reason.trim().length > 0;
  const canSubmit = amountValid && reasonValid && !issue.isPending;

  const kindOptions = useMemo(
    () => KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    [],
  );

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    const body: CreateCreditNoteRequest = {
      kind,
      reason: reason.trim(),
      ...(kind === 'PARTIAL' && typeof amount === 'number'
        ? { creditedAmountPaise: rupeesToPaise(amount) }
        : {}),
    };
    issue.mutate(body, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Issue credit note"
      description="A full note credits the whole remaining invoice; a partial note credits a gross (GST-inclusive) amount."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </UzButton>
          <UzButton onClick={handleSubmit} disabled={!canSubmit} loading={issue.isPending}>
            Issue credit note
          </UzButton>
        </div>
      }
    >
      <div className="space-y-4">
        <UzSelect
          label="Kind"
          value={kind}
          onValueChange={(v) => setKind(v as CreditNoteKind)}
          options={kindOptions}
        />
        {kind === 'PARTIAL' && (
          <UzMoneyInput
            label="Credit amount (gross)"
            required
            value={amount}
            onValueChange={(v) => setAmount(v ?? '')}
          />
        )}
        <UzInput
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Trip cancelled by customer"
        />
      </div>
    </UzDialog>
  );
};
